import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, Cell,
} from 'recharts';
import api from '../services/api';
import './JDMAAnalyseIA.css';

// Nombre de thèmes empilés avant regroupement. La palette catégorielle en
// compte 8 : au-delà, on ne génère pas de 9e teinte, on replie dans « Autres ».
const MAX_SERIES = 8;
const AUTRES = 'Autres';

const TYPES = [
  { value: 'apv', label: '🔧 Atelier (APV)' },
  { value: 'vn', label: '🚗 Ventes VN' },
];

// Le négatif et le positif se lisent de la même façon : même graphique, même
// palette, seule la mesure change.
const MODES = [
  { value: 'negatif', label: '😕 Mentions négatives', champ: 'nb_negatif', titre: 'Mentions négatives par thème et par mois' },
  { value: 'positif', label: '🙂 Mentions positives', champ: 'nb_positif', titre: 'Mentions positives par thème et par mois' },
  { value: 'total', label: '· Toutes mentions', champ: 'nb_total', titre: 'Mentions par thème et par mois' },
];

/** 12 derniers mois, du plus récent au plus ancien. */
function derniersMois(n = 12) {
  const out = [];
  const now = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(Date.UTC(now.getFullYear(), now.getMonth() - i, 1));
    const value = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric', timeZone: 'UTC' });
    out.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
  }
  return out;
}

/** 'YYYY-MM' du mois calendaire courant — repère du « mois en cours ». */
function moisCourant() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function formatMois(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit', timeZone: 'UTC' });
}

/** Libellé long d'un mois 'YYYY-MM' ('Septembre 2026'). */
function libelleMois(ym) {
  if (!ym) return '';
  const [y, m] = ym.split('-').map(Number);
  const l = new Date(Date.UTC(y, m - 1, 1))
    .toLocaleDateString('fr-FR', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  return l.charAt(0).toUpperCase() + l.slice(1);
}

/** « de septembre 2026 » / « d'août 2026 » — avril, août et octobre s'élident. */
function duMois(ym) {
  const l = libelleMois(ym).toLowerCase();
  return `${/^[aeiouâàéèêîôûy]/.test(l) ? "d'" : 'de '}${l}`;
}

/**
 * Écart entre deux valeurs, rendu comme un texte signé — jamais une couleur
 * seule : une hausse d'avis est neutre, une baisse de note ne l'est pas, et
 * la flèche seule ne dit pas de combien.
 */
function ecart(actuel, precedent, { decimales = 0 } = {}) {
  if (typeof actuel !== 'number' || typeof precedent !== 'number' || precedent === 0) return null;
  const d = actuel - precedent;
  if (Math.abs(d) < (decimales ? 0.005 : 0.5)) return { texte: '=', sens: 'stable' };
  const v = Math.abs(d).toFixed(decimales).replace('.', ',');
  return { texte: `${d > 0 ? '+' : '−'}${v}`, sens: d > 0 ? 'hausse' : 'baisse' };
}

function formatNombre(n) {
  return typeof n === 'number' ? n.toLocaleString('fr-FR') : '—';
}

function formatNote(n) {
  return typeof n === 'number' ? n.toFixed(2).replace('.', ',') : '—';
}

/**
 * Gabarit FIXE du texte copié. C'est lui — et pas le modèle — qui garantit
 * qu'une synthèse se lit de la même façon d'une concession à l'autre : mêmes
 * sections, même ordre, chiffres issus des agrégats SQL.
 */
export function buildTexteSynthese({ resultat, agregats, type, concessionNom }) {
  const v = agregats?.volumes || {};
  const prec = agregats?.mois_precedent || {};
  const L = [];
  const titre = type === 'vn' ? 'VENTES VN' : 'ATELIER (APV)';

  L.push(`SYNTHÈSE QUALITÉ CLIENT — ${titre}`);
  L.push(`Concession : ${concessionNom || 'Toutes concessions'}`);
  L.push(`Période : ${agregats?.periode?.libelle || '—'}`);
  L.push('');

  L.push('INDICATEURS');
  L.push(`- Avis reçus : ${formatNombre(v.total_avis)} (dont ${formatNombre(v.avec_commentaire)} avec commentaire, ${formatNombre(v.analyses)} analysés)`);
  L.push(`- Note moyenne : ${formatNote(v.note_moyenne)}/5${prec.note_moyenne != null ? `  (${prec.libelle} : ${formatNote(prec.note_moyenne)})` : ''}`);
  L.push(`- Avis 5 étoiles : ${v.pct_5 ?? 0} %  ·  Avis 3 étoiles ou moins : ${v.pct_alerte ?? 0} %`);
  L.push('');

  L.push('SYNTHÈSE');
  L.push(resultat?.synthese_globale || '—');
  L.push('');

  const compteurs = new Map((agregats?.themes || []).map(t => [t.theme, t]));

  L.push('IRRITANTS PRINCIPAUX');
  if (!resultat?.irritants?.length) {
    L.push('- Aucun irritant significatif identifié sur la période.');
  } else {
    resultat.irritants.forEach((it, i) => {
      const c = compteurs.get(it.theme);
      const chiffre = c ? ` — ${formatNombre(c.nb_negatif)} mention(s) négative(s) sur ${formatNombre(c.mentions)}` : '';
      L.push(`${i + 1}. [${(it.gravite || 'orange').toUpperCase()}] ${it.theme}${chiffre}`);
      if (it.constat) L.push(`   ${it.constat}`);
      if (it.verbatim) L.push(`   « ${it.verbatim} »`);
    });
  }
  L.push('');

  L.push('POINTS POSITIFS');
  if (!resultat?.points_positifs?.length) {
    L.push('- Aucun point positif marquant identifié sur la période.');
  } else {
    resultat.points_positifs.forEach((p, i) => {
      const c = compteurs.get(p.theme);
      const chiffre = c ? ` — ${formatNombre(c.nb_positif)} mention(s) positive(s) sur ${formatNombre(c.mentions)}` : '';
      L.push(`${i + 1}. ${p.theme}${chiffre}`);
      if (p.constat) L.push(`   ${p.constat}`);
      if (p.verbatim) L.push(`   « ${p.verbatim} »`);
    });
  }
  L.push('');

  L.push('SIGNAUX À SURVEILLER');
  if (!resultat?.signaux_a_surveiller?.length) L.push('- Néant.');
  else resultat.signaux_a_surveiller.forEach(s => L.push(`- ${s}`));
  L.push('');

  L.push('RECOMMANDATIONS');
  if (!resultat?.recommandations?.length) L.push('- Néant.');
  else resultat.recommandations.forEach((r, i) => L.push(`${i + 1}. ${r}`));
  L.push('');

  L.push('---');
  L.push('Synthèse générée automatiquement à partir des enquêtes JDMA. Les commentaires sont analysés de façon anonymisée.');

  return L.join('\n');
}

async function copierTexte(texte) {
  try {
    await navigator.clipboard.writeText(texte);
    return true;
  } catch {
    // navigator.clipboard n'existe pas en contexte non sécurisé (HTTP local).
    const ta = document.createElement('textarea');
    ta.value = texte;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try { ok = document.execCommand('copy'); } catch { /* ignore */ }
    document.body.removeChild(ta);
    return ok;
  }
}

function JDMAAnalyseIA({ user, concessions = [], editMode = false }) {
  const moisDispo = useMemo(() => derniersMois(12), []);
  const moisEnCours = useMemo(() => moisCourant(), []);

  const [type, setType] = useState('apv');
  // Concession : celle choisie dans le menu (2026-10-26) ; la page est remontée
  // à chaque changement du menu.
  const [concessionId] = useState(() => (user?.concession_id ? String(user.concession_id) : ''));
  const [month, setMonth] = useState(moisEnCours);
  const [mode, setMode] = useState('negatif');
  const [showTable, setShowTable] = useState(false);
  const [histoOuvert, setHistoOuvert] = useState(false);

  // Le recentrage automatique sur le dernier mois alimenté ne doit jouer qu'une
  // fois par contexte : passé ce point, le mois affiché est un choix de
  // l'utilisateur, et le lui reprendre à chaque rechargement serait pire que
  // l'écran vide qu'on cherche à éviter.
  const recentrageFait = useRef('');

  const [etat, setEtat] = useState(null);
  const [evolution, setEvolution] = useState(null);
  const [synthese, setSynthese] = useState(null);
  const [historique, setHistorique] = useState([]);

  const [loading, setLoading] = useState(true);
  const [etape, setEtape] = useState(null);   // null | 'analyse' | 'synthese'
  const [erreur, setErreur] = useState(null);
  const [copie, setCopie] = useState(false);
  const [toast, setToast] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const isSuperAdmin = user?.role === 'super-admin';
  const concessionNom = concessionId
    ? (user?.concession_nom || concessions.find(c => String(c.id) === String(concessionId))?.nom || null)
    : null;

  const showToast = (msg, kind = 'success') => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 4000);
  };

  const chargerHistorique = useCallback(() => {
    const params = new URLSearchParams({ limit: '10' });
    if (concessionId) params.set('concession_id', concessionId);
    api.get(`/jdma/${type}/analyse/synthese/history?${params}`)
      .then(r => setHistorique(r.data.syntheses || []))
      .catch(() => setHistorique([]));
  }, [type, concessionId]);

  // Chargement de l'état du mois + de la série du graphique.
  useEffect(() => {
    const ac = new AbortController();
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErreur(null);
      try {
        const p = new URLSearchParams({ month });
        const pe = new URLSearchParams({ months: '12' });
        if (concessionId) { p.set('concession_id', concessionId); pe.set('concession_id', concessionId); }
        const opt = { signal: ac.signal };

        const [etatRes, evoRes] = await Promise.all([
          api.get(`/jdma/${type}/analyse/etat?${p}`, opt),
          api.get(`/jdma/${type}/analyse/themes-evolution?${pe}`, opt),
        ]);
        if (cancelled || ac.signal.aborted) return;
        setEtat(etatRes.data);
        setEvolution(evoRes.data);
      } catch (err) {
        if (cancelled || ac.signal.aborted) return;
        if (err.name === 'CanceledError' || err.name === 'AbortError' || err.code === 'ERR_CANCELED') return;
        console.error('Erreur chargement Analyse IA:', err);
        setErreur(err.response?.data?.error || 'Erreur de chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; ac.abort(); };
  }, [type, month, concessionId, refreshKey]);

  // Recentrage : si le mois en cours n'a encore aucun avis (cas normal les
  // premiers jours), on bascule sur le dernier mois réellement alimenté plutôt
  // que d'ouvrir la page sur des zéros et une action inerte.
  useEffect(() => {
    if (!etat || month !== moisEnCours) return;
    const cle = `${type}|${concessionId}`;
    if (recentrageFait.current === cle) return;
    const cible = etat.dernier_mois_avec_avis;
    if ((etat.total_avis || 0) === 0 && cible && cible !== moisEnCours
        && moisDispo.some(m => m.value === cible)) {
      recentrageFait.current = cle;
      setMonth(cible);
    }
  }, [etat, month, moisEnCours, type, concessionId, moisDispo]);

  // La synthèse affichée ne vaut que pour le triplet courant.
  useEffect(() => { setSynthese(null); setCopie(false); }, [type, month, concessionId]);
  useEffect(() => { chargerHistorique(); }, [chargerHistorique, refreshKey]);

  // ── Bouton unique : analyse des commentaires manquants, puis synthèse ──
  const handleGenerer = async (force = false) => {
    if (!isSuperAdmin || etape) return;
    setErreur(null);
    try {
      const periode = etat?.periode;
      if ((etat?.a_analyser || 0) > 0 && periode) {
        setEtape('analyse');
        // Les deux bornes portent une heure explicite : une date nue
        // ('2026-09-01') est parsée en UTC alors que '…T23:59:59' l'est en
        // local, ce qui décalerait la borne basse de plusieurs heures.
        const p = new URLSearchParams({
          from: `${periode.debut}T00:00:00`,
          to: `${periode.fin}T23:59:59`,
        });
        if (concessionId) p.set('concession_id', concessionId);
        await api.post(`/jdma/${type}/analyse/run?${p}`);
      }

      setEtape('synthese');
      const res = await api.post(`/jdma/${type}/analyse/synthese`, {
        month,
        concession_id: concessionId ? parseInt(concessionId) : null,
        force,
      });
      setSynthese(res.data);
      setRefreshKey(k => k + 1);
      showToast(res.data.cached ? 'Synthèse récupérée depuis le cache (24 h)' : 'Synthèse générée');
    } catch (err) {
      console.error('Erreur génération synthèse:', err);
      const msg = err.response?.data?.error || 'Erreur de génération';
      setErreur(msg);
      showToast(msg, 'error');
    } finally {
      setEtape(null);
    }
  };

  const handleCharger = async (id) => {
    try {
      const res = await api.get(`/jdma/${type}/analyse/synthese/${id}`);
      setSynthese(res.data);
      setCopie(false);
    } catch {
      showToast('Impossible de charger cette synthèse', 'error');
    }
  };

  const handleSupprimer = async (id) => {
    if (!window.confirm('Supprimer définitivement cette synthèse ?')) return;
    try {
      await api.delete(`/jdma/${type}/analyse/synthese/${id}`);
      if (synthese?.id === id) setSynthese(null);
      setRefreshKey(k => k + 1);
      showToast('Synthèse supprimée');
    } catch {
      showToast('Suppression impossible', 'error');
    }
  };

  const handleCopier = async () => {
    const texte = buildTexteSynthese({
      resultat: synthese.resultat,
      agregats: synthese.agregats,
      type,
      concessionNom: synthese.concession_nom || concessionNom,
    });
    const ok = await copierTexte(texte);
    if (ok) {
      setCopie(true);
      setTimeout(() => setCopie(false), 1500);
    } else {
      showToast('Copie impossible — sélectionnez le texte manuellement', 'error');
    }
  };

  // ── Données du graphique ──
  // Les couleurs sont indexées sur `palette_ordre`, un classement global figé
  // côté serveur : filtrer par concession ne repeint donc jamais les séries.
  const { series, chartData, tableRows } = useMemo(() => {
    if (!evolution?.rows?.length) return { series: [], chartData: [], tableRows: [] };

    const champ = MODES.find(m => m.value === mode)?.champ || 'nb_negatif';
    const ordre = evolution.palette_ordre || evolution.themes || [];
    const retenus = ordre.filter(t => (evolution.themes || []).includes(t)).slice(0, MAX_SERIES);
    const setRetenus = new Set(retenus);
    const aDesAutres = (evolution.themes || []).some(t => !setRetenus.has(t));
    const cols = aDesAutres ? [...retenus, AUTRES] : retenus;

    const parMois = new Map(evolution.mois.map(m => [m, { mois: m, mois_label: formatMois(m) }]));
    for (const r of evolution.rows) {
      const ligne = parMois.get(r.mois);
      if (!ligne) continue;
      const cle = setRetenus.has(r.theme) ? r.theme : AUTRES;
      ligne[cle] = (ligne[cle] || 0) + (r[champ] || 0);
    }
    const data = evolution.mois.map(m => {
      const l = parMois.get(m);
      for (const c of cols) if (l[c] == null) l[c] = 0;
      return l;
    });

    return { series: cols, chartData: data, tableRows: data };
  }, [evolution, mode]);

  const totalGraphique = useMemo(
    () => chartData.reduce((s, l) => s + series.reduce((a, c) => a + (l[c] || 0), 0), 0),
    [chartData, series]
  );

  // ── Thèmes du mois focalisé ──
  // Barres horizontales triées, une couleur par polarité : comparer des
  // grandeurs entre catégories est ce que l'empilement rendait justement
  // impossible. Même source (`themes-evolution`) filtrée sur le mois affiché,
  // donc aucun appel supplémentaire.
  const themesMois = useMemo(() => {
    if (!evolution?.rows?.length) return [];
    const cle = `${month}-01`;
    const champ = MODES.find(m => m.value === mode)?.champ || 'nb_negatif';
    return evolution.rows
      .filter(r => r.mois === cle && (r[champ] || 0) > 0)
      .map(r => ({
        theme: r.theme,
        valeur: r[champ] || 0,
        nb_total: r.nb_total || 0,
        nb_negatif: r.nb_negatif || 0,
        nb_positif: r.nb_positif || 0,
      }))
      .sort((a, b) => b.valeur - a.valeur);
  }, [evolution, month, mode]);

  const maxThemeMois = themesMois.length ? themesMois[0].valeur : 0;

  // Une barre par thème + son en-tête : en dessous, le graphique serait plus
  // haut que ses données.
  const hauteurThemes = Math.max(140, themesMois.length * 34 + 24);

  const tendanceAvis = ecart(etat?.total_avis, etat?.mois_precedent?.total_avis);
  const tendanceNote = ecart(etat?.note_moyenne, etat?.mois_precedent?.note_moyenne, { decimales: 2 });

  const moisLabel = libelleMois(month);
  const estMoisEnCours = month === moisEnCours;
  const peutGenerer = (etat?.avec_commentaire || 0) > 0;

  const couleurSerie = (nom, i) =>
    nom === AUTRES ? 'var(--jdma-cat-autres)' : `var(--jdma-cat-${(i % MAX_SERIES) + 1})`;

  const v = synthese?.agregats?.volumes;
  const compteursTheme = useMemo(
    () => new Map((synthese?.agregats?.themes || []).map(t => [t.theme, t])),
    [synthese]
  );

  return (
    <div className="jdma-ia">
      {/* 1. Toolbar — recherche/filtres uniquement, une seule ligne au-dessus de tout */}
      <div className="jdma-ia-toolbar">
        <select className="page-select" value={type} onChange={e => setType(e.target.value)} title="Type d'enquête">
          {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        {/* Concession : choix du menu (2026-10-26), plus de liste propre à la page. */}
        <select
          className={`page-select ${month !== moisEnCours ? 'active-filter' : ''}`}
          value={month}
          onChange={e => setMonth(e.target.value)}
          title="Mois analysé"
        >
          {moisDispo.map(m => (
            <option key={m.value} value={m.value}>
              {m.label}{m.value === moisEnCours ? ' (mois en cours)' : ''}
            </option>
          ))}
        </select>
        <select className="page-select" value={mode} onChange={e => setMode(e.target.value)} title="Mesure affichée dans le graphique">
          {MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
      </div>

      {erreur && <div className="jdma-ia-erreur">⚠️ {erreur}</div>}

      {/* 2. SECTION PRINCIPALE — le mois focalisé.
          C'est le seul bloc qui répond à « où en est-on ce mois-ci ? » : les
          indicateurs du mois, ses thèmes, et l'action qui produit la synthèse.
          Tout ce qui relève de la comparaison dans le temps est renvoyé plus
          bas, en section secondaire. */}
      <section className="jdma-ia-card jdma-ia-focus" aria-labelledby="jdma-ia-focus-titre">
        <div className="jdma-ia-focus-head">
          <div className="jdma-ia-focus-ident">
            <div className="jdma-ia-eyebrow">
              <span className={`jdma-ia-pastille ${estMoisEnCours ? 'en-cours' : 'clos'}`} />
              {estMoisEnCours ? 'Mois en cours' : 'Mois clos'}
              <span className="jdma-ia-sep">·</span>
              {concessionNom || 'Toutes concessions'}
              <span className="jdma-ia-sep">·</span>
              {type === 'vn' ? 'Ventes VN' : 'Atelier (APV)'}
            </div>
            <h3 className="jdma-ia-focus-titre" id="jdma-ia-focus-titre">{moisLabel}</h3>
          </div>

          {peutGenerer && (
            <button
              className="jdma-ia-btn"
              onClick={() => handleGenerer(false)}
              disabled={!!etape}
              title={`Analyse les commentaires manquants puis rédige la synthèse ${duMois(month)}`}
            >
              {etape === 'analyse' ? `Analyse de ${formatNombre(etat?.a_analyser)} commentaires…`
                : etape === 'synthese' ? 'Génération de la synthèse…'
                : `Générer la synthèse ${duMois(month)}`}
            </button>
          )}
        </div>

        {loading && !etat ? (
          <div className="jdma-ia-empty">Chargement…</div>
        ) : (etat?.total_avis || 0) === 0 ? (
          /* Mois sans aucun avis : on explique la cause et on propose la
             sortie, plutôt que d'afficher des zéros surmontés d'un bouton
             grisé qui ne dit pas pourquoi il l'est. */
          <div className="jdma-ia-vide">
            <p className="jdma-ia-vide-titre">Aucun avis reçu sur {moisLabel.toLowerCase()}.</p>
            <p className="jdma-ia-vide-texte">
              {estMoisEnCours
                ? 'Les enquêtes du mois ne sont pas encore remontées. La synthèse pourra être générée dès les premiers commentaires.'
                : 'Aucune enquête n’a été enregistrée sur ce mois.'}
            </p>
            {etat?.dernier_mois_avec_avis && etat.dernier_mois_avec_avis !== month
              && moisDispo.some(m => m.value === etat.dernier_mois_avec_avis) && (
              <button
                className="jdma-ia-btn jdma-ia-btn-secondaire"
                onClick={() => setMonth(etat.dernier_mois_avec_avis)}
              >
                Voir {libelleMois(etat.dernier_mois_avec_avis).toLowerCase()}
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Indicateurs du mois — la valeur porte le sens, l'écart avec le
                mois précédent est écrit en toutes lettres et pas signifié par
                la seule couleur. */}
            <dl className="jdma-ia-kpis">
              <div className="jdma-ia-kpi">
                <dt>Avis reçus</dt>
                <dd>
                  {formatNombre(etat?.total_avis)}
                  {tendanceAvis && (
                    <span className={`jdma-ia-delta ${tendanceAvis.sens}`}>
                      {tendanceAvis.texte} vs mois précédent
                    </span>
                  )}
                </dd>
              </div>
              <div className="jdma-ia-kpi">
                <dt>Note moyenne</dt>
                <dd>
                  {formatNote(etat?.note_moyenne)}<span className="jdma-ia-unite">/5</span>
                  {tendanceNote && (
                    <span className={`jdma-ia-delta ${tendanceNote.sens}`}>
                      {tendanceNote.texte} vs mois précédent
                    </span>
                  )}
                </dd>
              </div>
              <div className="jdma-ia-kpi">
                <dt>Avis 5 étoiles</dt>
                <dd>{etat?.pct_5 ?? 0}<span className="jdma-ia-unite">%</span></dd>
              </div>
              <div className="jdma-ia-kpi jdma-ia-kpi-alerte">
                <dt>Avis à 3 étoiles ou moins</dt>
                <dd>{etat?.pct_alerte ?? 0}<span className="jdma-ia-unite">%</span></dd>
              </div>
            </dl>

            <p className="jdma-ia-pipeline">
              <strong>{formatNombre(etat?.avec_commentaire)}</strong> avis avec commentaire
              <span className="jdma-ia-sep">·</span>
              <strong>{formatNombre(etat?.analyses)}</strong> déjà analysés
              {(etat?.a_analyser || 0) > 0 && (
                <>
                  <span className="jdma-ia-sep">·</span>
                  <span className="jdma-ia-reste">{formatNombre(etat.a_analyser)} à analyser</span>
                </>
              )}
              {!peutGenerer && ' — aucun commentaire exploitable, la synthèse n’aurait pas de matière.'}
            </p>

            {/* Thèmes du mois — barres horizontales triées : la longueur se
                compare d'un coup d'œil, et chaque valeur est écrite. */}
            <div className="jdma-ia-themes">
              <h4 className="jdma-ia-sous-titre">
                {mode === 'negatif' ? 'Thèmes les plus critiqués ce mois-ci'
                  : mode === 'positif' ? 'Thèmes les plus appréciés ce mois-ci'
                  : 'Thèmes les plus mentionnés ce mois-ci'}
              </h4>
              {!themesMois.length ? (
                <div className="jdma-ia-empty">
                  {(etat?.analyses || 0) === 0
                    ? 'Les commentaires du mois ne sont pas encore analysés — lancez la génération ci-dessus.'
                    : `Aucune mention de ce type ce mois-ci — essayez « ${MODES.find(m => m.value !== mode)?.label} ».`}
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={hauteurThemes}>
                  <BarChart
                    data={themesMois}
                    layout="vertical"
                    margin={{ top: 4, right: 44, left: 4, bottom: 4 }}
                  >
                    <CartesianGrid horizontal={false} stroke="var(--border-default)" />
                    <XAxis type="number" hide domain={[0, maxThemeMois]} />
                    <YAxis
                      type="category"
                      dataKey="theme"
                      width={170}
                      tickLine={false}
                      axisLine={false}
                      stroke="var(--text-secondary)"
                    />
                    <Tooltip
                      cursor={{ fill: 'var(--bg-hover)' }}
                      contentStyle={{
                        background: 'var(--bg-surface)',
                        border: '1px solid var(--border-default)',
                        borderRadius: 8,
                        color: 'var(--text-primary)',
                      }}
                      itemStyle={{ color: 'var(--text-primary)' }}
                      labelStyle={{ color: 'var(--text-secondary)' }}
                      formatter={(valeur, _n, p) => [
                        `${formatNombre(valeur)} sur ${formatNombre(p?.payload?.nb_total)} mentions`,
                        MODES.find(m => m.value === mode)?.label.replace(/^\S+\s/, ''),
                      ]}
                    />
                    <Bar
                      dataKey="valeur"
                      radius={[0, 4, 4, 0]}
                      barSize={18}
                      isAnimationActive={false}
                      label={{
                        position: 'right',
                        fill: 'var(--text-primary)',
                        fontSize: 12,
                        formatter: (v) => formatNombre(v),
                      }}
                    >
                      {/* Une seule teinte par polarité : ici la couleur ne
                          distingue pas des séries, elle qualifie la mesure. */}
                      {themesMois.map(t => (
                        <Cell
                          key={t.theme}
                          fill={mode === 'negatif' ? 'var(--color-error)'
                            : mode === 'positif' ? 'var(--color-success)'
                            : 'var(--color-info)'}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </>
        )}
      </section>

      {/* 3. Synthèse */}
      {synthese && (
        <div className="jdma-ia-card jdma-ia-synthese">
          <div className="jdma-ia-card-head">
            <h3 className="jdma-ia-card-title">
              🤖 Synthèse — {synthese.agregats?.periode?.libelle}
              {(synthese.concession_nom || concessionNom) ? ` · ${synthese.concession_nom || concessionNom}` : ' · Toutes concessions'}
            </h3>
            <div className="jdma-ia-head-actions">
              {synthese.cached && (
                <span className="jdma-ia-cache">
                  cache · {new Date(synthese.created_at).toLocaleString('fr-FR')}
                  <button className="jdma-ia-link-btn" onClick={() => handleGenerer(true)} disabled={!!etape}>↻ Relancer</button>
                </span>
              )}
              <button className="btn-secondary" onClick={handleCopier}>
                {copie ? '✓ Copié' : '📋 Copier la synthèse'}
              </button>
            </div>
          </div>

          <div className="jdma-ia-indicateurs">
            <span><strong>{formatNombre(v?.total_avis)}</strong> avis</span>
            <span><strong>{formatNote(v?.note_moyenne)}</strong>/5</span>
            <span><strong>{v?.pct_5 ?? 0} %</strong> de 5★</span>
            <span><strong>{v?.pct_alerte ?? 0} %</strong> à 3★ ou moins</span>
          </div>

          <p className="jdma-ia-globale">{synthese.resultat?.synthese_globale || '—'}</p>

          <div className="jdma-ia-colonnes">
            <div>
              <h4>Irritants principaux</h4>
              {synthese.resultat?.irritants?.length ? (
                <ul className="jdma-ia-liste">
                  {synthese.resultat.irritants.map((it, i) => {
                    const c = compteursTheme.get(it.theme);
                    return (
                      <li key={i} className={`jdma-ia-gravite-${it.gravite || 'orange'}`}>
                        <div className="jdma-ia-item-head">
                          <span className="jdma-ia-dot" />
                          <strong>{it.theme}</strong>
                          {c && <span className="jdma-ia-compteur">{formatNombre(c.nb_negatif)} / {formatNombre(c.mentions)} mentions</span>}
                        </div>
                        <div className="jdma-ia-constat">{it.constat}</div>
                        {it.verbatim && <blockquote className="jdma-ia-verbatim">« {it.verbatim} »</blockquote>}
                      </li>
                    );
                  })}
                </ul>
              ) : <div className="jdma-ia-empty">Aucun irritant significatif.</div>}
            </div>

            <div>
              <h4>Points positifs</h4>
              {synthese.resultat?.points_positifs?.length ? (
                <ul className="jdma-ia-liste">
                  {synthese.resultat.points_positifs.map((p, i) => {
                    const c = compteursTheme.get(p.theme);
                    return (
                      <li key={i} className="jdma-ia-gravite-vert">
                        <div className="jdma-ia-item-head">
                          <span className="jdma-ia-dot" />
                          <strong>{p.theme}</strong>
                          {c && <span className="jdma-ia-compteur">{formatNombre(c.nb_positif)} / {formatNombre(c.mentions)} mentions</span>}
                        </div>
                        <div className="jdma-ia-constat">{p.constat}</div>
                        {p.verbatim && <blockquote className="jdma-ia-verbatim">« {p.verbatim} »</blockquote>}
                      </li>
                    );
                  })}
                </ul>
              ) : <div className="jdma-ia-empty">Aucun point positif marquant.</div>}
            </div>
          </div>

          {!!synthese.resultat?.signaux_a_surveiller?.length && (
            <div className="jdma-ia-bloc">
              <h4>Signaux à surveiller</h4>
              <ul>{synthese.resultat.signaux_a_surveiller.map((s, i) => <li key={i}>{s}</li>)}</ul>
            </div>
          )}

          {!!synthese.resultat?.recommandations?.length && (
            <div className="jdma-ia-bloc">
              <h4>Recommandations</h4>
              <ol>{synthese.resultat.recommandations.map((r, i) => <li key={i}>{r}</li>)}</ol>
            </div>
          )}

          <div className="jdma-ia-meta">
            {synthese.modele}
            {synthese.tokens_in != null && ` · ${formatNombre(synthese.tokens_in)} in / ${formatNombre(synthese.tokens_out)} out`}
            {synthese.cout_estime_usd != null && ` · ≈ ${(synthese.cout_estime_usd * 0.93).toFixed(3)} €`}
          </div>
        </div>
      )}

      {/* 4. SECTION SECONDAIRE — l'historique.
          Repliée par défaut : la mise en perspective sur douze mois est une
          question qu'on se pose après avoir lu le mois, pas avant. C'est aussi
          ce qui libère le haut de page, où l'empilement à huit séries occupait
          la place du mois en cours sans être lisible pour autant. */}
      <section className="jdma-ia-card jdma-ia-histo">
        <button
          className="jdma-ia-histo-toggle"
          onClick={() => setHistoOuvert(o => !o)}
          aria-expanded={histoOuvert}
          aria-controls="jdma-ia-histo-corps"
        >
          <span className={`jdma-ia-chevron ${histoOuvert ? 'ouvert' : ''}`} aria-hidden="true">›</span>
          <span className="jdma-ia-card-title">Historique</span>
          <span className="jdma-ia-histo-resume">
            Évolution sur 12 mois
            {historique.length > 0 && ` · ${historique.length} synthèse${historique.length > 1 ? 's' : ''} enregistrée${historique.length > 1 ? 's' : ''}`}
          </span>
        </button>

        {histoOuvert && (
          <div id="jdma-ia-histo-corps" className="jdma-ia-histo-corps">
            <div className="jdma-ia-card-head">
              <h4 className="jdma-ia-sous-titre">{MODES.find(m => m.value === mode)?.titre}</h4>
              {!!chartData.length && totalGraphique > 0 && (
                <button className="jdma-ia-link-btn" onClick={() => setShowTable(s => !s)}>
                  {showTable ? 'Masquer les données' : 'Voir les données'}
                </button>
              )}
            </div>

            {!chartData.length || totalGraphique === 0 ? (
              <div className="jdma-ia-empty">
                {chartData.length
                  ? `Aucune mention de ce type sur les 12 derniers mois — essayez « ${MODES.find(m => m.value !== mode)?.label} ».`
                  : "Aucune analyse sur les 12 derniers mois. Lancez d'abord une génération pour alimenter le graphique."}
              </div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                    <CartesianGrid vertical={false} stroke="var(--border-default)" />
                    <XAxis dataKey="mois_label" stroke="var(--text-secondary)" tickLine={false} />
                    <YAxis stroke="var(--text-secondary)" tickLine={false} allowDecimals={false} />
                    <Tooltip
                      cursor={{ fill: 'var(--bg-hover)' }}
                      contentStyle={{
                        background: 'var(--bg-surface)',
                        border: '1px solid var(--border-default)',
                        borderRadius: 8,
                        color: 'var(--text-primary)',
                      }}
                      /* Par défaut recharts colore le texte à la couleur de la
                         série ; le texte doit rester sur les tokens d'encre, la
                         pastille colorée portant seule l'identité. */
                      itemStyle={{ color: 'var(--text-primary)' }}
                      labelStyle={{ color: 'var(--text-secondary)' }}
                    />
                    <Legend
                      formatter={(value) => <span style={{ color: 'var(--text-secondary)' }}>{value}</span>}
                    />
                    {series.map((nom, i) => (
                      <Bar
                        key={nom}
                        dataKey={nom}
                        stackId="a"
                        name={nom}
                        fill={couleurSerie(nom, i)}
                        /* Écart de 2px entre segments empilés : un trait de la
                           couleur du fond, pas une bordure autour des marques. */
                        stroke="var(--bg-surface)"
                        strokeWidth={2}
                        radius={i === series.length - 1 ? [4, 4, 0, 0] : 0}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>

                {/* Vue tableau — obligatoire : trois teintes de la palette passent
                    sous 3:1 sur fond clair, la lecture ne peut pas reposer sur la
                    seule couleur. */}
                {showTable && (
                  <div className="table-container jdma-ia-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Thème</th>
                          {chartData.map(l => <th key={l.mois}>{l.mois_label}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {series.map(nom => (
                          <tr key={nom}>
                            <th scope="row">{nom}</th>
                            {tableRows.map(l => <td key={l.mois}>{formatNombre(l[nom] || 0)}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            <div className="jdma-ia-bloc">
              <h4 className="jdma-ia-sous-titre">Synthèses précédentes</h4>
              {!historique.length ? (
                <div className="jdma-ia-empty">Aucune synthèse enregistrée pour ce périmètre.</div>
              ) : (
                <ul className="jdma-ia-historique">
                  {historique.map(h => (
                    <li key={h.id}>
                      <button className="jdma-ia-link-btn" onClick={() => handleCharger(h.id)}>
                        {libelleMois(h.periode_debut?.slice(0, 7))} · {h.concession_nom || 'Toutes concessions'}
                      </button>
                      <span className="jdma-ia-hist-meta">
                        {new Date(h.created_at).toLocaleString('fr-FR')}
                        {h.cout_estime_usd != null && ` · ≈ ${(h.cout_estime_usd * 0.93).toFixed(3)} €`}
                      </span>
                      {editMode && isSuperAdmin && (
                        <div className="action-buttons">
                          <button className="btn-icon delete" title="Supprimer" onClick={() => handleSupprimer(h.id)}>🗑️</button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </section>

      {toast && <div className={`jdma-ia-toast ${toast.kind}`}>{toast.msg}</div>}
    </div>
  );
}

export default JDMAAnalyseIA;
